import { UserAccessError, type UserAccessErrorCode } from './types';

/**
 * Map a raw Firebase Auth error to our package-owned UserAccessError.
 *
 * Any Firebase code not in the table collapses to 'unknown'. We deliberately
 * don't try to preserve Firebase's messages verbatim — they're inconsistent
 * across SDK versions. Human-facing text is the consumer app's concern.
 */
export function mapFirebaseError(err: unknown): UserAccessError {
  if (err instanceof UserAccessError) return err;

  const code = extractFirebaseCode(err);
  const normalized = FIREBASE_CODE_MAP[code] ?? 'unknown';
  const message = codeToDefaultMessage(normalized);
  return new UserAccessError(normalized, message);
}

function extractFirebaseCode(err: unknown): string {
  if (!err || typeof err !== 'object') return '';
  const maybeCode = (err as { code?: unknown }).code;
  return typeof maybeCode === 'string' ? maybeCode : '';
}

const FIREBASE_CODE_MAP: Record<string, UserAccessErrorCode> = {
  'auth/invalid-email': 'invalid-email',
  'auth/user-disabled': 'user-disabled',
  'auth/user-not-found': 'user-not-found',
  'auth/wrong-password': 'wrong-password',
  'auth/invalid-credential': 'wrong-password',
  'auth/email-already-in-use': 'email-in-use',
  'auth/weak-password': 'weak-password',
  'auth/password-does-not-meet-requirements': 'weak-password',
  'auth/operation-not-allowed': 'operation-not-allowed',
  'auth/too-many-requests': 'too-many-requests',
  'auth/network-request-failed': 'network',
  'auth/popup-blocked': 'popup-blocked',
  'auth/popup-closed-by-user': 'popup-closed',
  'auth/cancelled-popup-request': 'popup-closed',
  'auth/unauthorized-continue-uri': 'invalid-continue-url',
  'auth/invalid-continue-uri': 'invalid-continue-url',
  'auth/missing-continue-uri': 'invalid-continue-url',
};

function codeToDefaultMessage(code: UserAccessErrorCode): string {
  switch (code) {
    case 'wrong-password':
      return 'Incorrect email or password.';
    case 'user-not-found':
      return 'No account with that email.';
    case 'email-in-use':
      return 'An account with this email already exists.';
    case 'invalid-email':
      return 'Invalid email address.';
    case 'weak-password':
      return 'Password does not meet requirements.';
    case 'operation-not-allowed':
      return 'Sign-in method is not enabled for this project.';
    case 'too-many-requests':
      return 'Too many attempts. Try again later.';
    case 'user-disabled':
      return 'This account has been disabled.';
    case 'popup-blocked':
      return 'Sign-in popup was blocked by the browser.';
    case 'popup-closed':
      return 'Sign-in popup was closed before completing.';
    case 'network':
      return 'Network error. Check your connection and try again.';
    case 'not-initialized':
      return 'User access not initialized. Call initUserAccess(firebaseConfig) at app startup.';
    case 'invalid-continue-url':
      // Names the fix, because this is a deploy misconfiguration rather than
      // anything the person signing up can act on.
      return 'The configured continueUrl is not an authorized domain for this Firebase project.';
    case 'unknown':
    default:
      return 'Authentication failed.';
  }
}
